import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    Card, 
    Typography, 
    Button, 
    Row, 
    Col, 
    Statistic, 
    List, 
    Avatar, 
    Badge, 
    Tag, 
    Timeline, 
    Empty, 
    Skeleton,
    Progress,
    Alert,
    Space,
    Divider,
    Tooltip,
    Rate,
    notification
} from 'antd';
import { 
    ShoppingCartOutlined, 
    CalendarOutlined, 
    StarOutlined, 
    UserOutlined, 
    ClockCircleOutlined,
    TrophyOutlined,
    HeartOutlined,
    BellOutlined,
    GiftOutlined,
    ThunderboltOutlined,
    RiseOutlined,
    FireOutlined,
    MessageOutlined,
    FileTextOutlined,
    ShopOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import RecommendationEngine from '../../UI/RecommendationEngine';
import GamificationSystem from '../../UI/GamificationSystem';
import CalendarIntegration from '../../UI/CalendarIntegration';
import pusherService from '../../../services/pusher';
import { getStatusColor as sharedStatusColor } from '../../../utils/ui';

const { Title, Text, Paragraph } = Typography;

function Dashboard() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [bookings, setBookings] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [leads, setLeads] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [favoriteVendors, setFavoriteVendors] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [stats, setStats] = useState({
        totalBookings: 0,
        pendingBookings: 0,
        completedBookings: 0,
        pendingRequests: 0,
        totalSpent: 0,
        loyaltyPoints: 0,
        savedAmount: 0
    });
    const [singleVendor, setSingleVendor] = useState(null);

    useEffect(() => {
        fetchDashboardData();
        (async () => {
            try {
                const res = await api.getVendors({ per_page: 2 });
                const list = Array.isArray(res.data?.data) ? res.data.data : (res.data || []);
                if (Array.isArray(list) && list.length === 1) {
                    setSingleVendor(list[0]);
                }
            } catch {}
        })();
        // Show welcome notification for new users
        if (user && !localStorage.getItem('dashboard_welcome_shown')) {
            notification.success({
                message: `Welcome ${user.name}!`,
                description: 'Explore our catering services and place your first order.',
                duration: 5,
                placement: 'topRight'
            });
            localStorage.setItem('dashboard_welcome_shown', 'true');
        }
        // Live refresh on relevant events
        let userChannel = null;
        (async () => {
            try {
                if (user) {
                    userChannel = await pusherService.subscribeToUser(user.id);
                    if (userChannel) {
                        const refresh = () => fetchDashboardData();
                        userChannel.bind('lead-status-update', refresh);
                        userChannel.bind('booking-updated', refresh);
                        userChannel.bind('new-message', refresh);
                    }
                }
            } catch (_) {}
        })();
        return () => { try { userChannel && userChannel.unbind_all && userChannel.unbind_all(); } catch (_) {} };
    }, [user]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [bookingsRes, draftsRes, leadsRes, quotesRes] = await Promise.all([
                api.getBookings({ per_page: 5 }),
                api.getBookingDraft(),
                api.getMyLeads({ per_page: 5 }),
                api.getQuotes()
            ]);
            
            const bookingsData = bookingsRes.data.data || [];
            setBookings(bookingsData);
            
            // Get all drafts for dashboard
            const draftsData = Array.isArray(draftsRes.data?.data)
                ? draftsRes.data.data
                : (draftsRes.data ? [draftsRes.data] : []);
            setDrafts(draftsData); // Show all drafts
            
            // Get leads (booking requests)
            const leadsData = leadsRes.data?.data || leadsRes.data || [];
            setLeads(leadsData);
            
            // Get quotes
            const quotesData = quotesRes.data?.data || quotesRes.data || [];
            setQuotes(quotesData);
            
            // Calculate stats
            const totalBookings = bookingsData.length;
            const pendingBookings = bookingsData.filter(b => b.status === 'pending').length;
            const completedBookings = bookingsData.filter(b => b.status === 'completed').length;
            const pendingRequests = leadsData.filter(lead => lead.status === 'pending').length;
            const totalSpent = bookingsData.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
            
            setStats({
                totalBookings,
                pendingBookings,
                completedBookings,
                pendingRequests,
                totalSpent,
                loyaltyPoints: Math.floor(totalSpent / 100), // 1 point per ₹100
                savedAmount: totalSpent * 0.05 // 5% savings simulation
            });

            // Fetch real recent activity
            try {
                const activityRes = await api.getActivities({ limit: 5 });
                if (activityRes.success) {
                    const activities = activityRes.data.map(activity => ({
                        id: activity.id,
                        action: activity.title,
                        details: activity.description,
                        time: activity.time_ago,
                        icon: getActivityIcon(activity.icon),
                        color: activity.color,
                        severity: activity.severity,
                        data: activity.data
                    }));
                    setRecentActivity(activities);
                }
            } catch (error) {
                console.error('Failed to fetch activities:', error);
                // Fallback to empty array if API fails
                setRecentActivity([]);
            }

            // Mock recommendations
            setRecommendations([
                { id: 1, title: 'Popular in your area', description: 'Royal Kitchen - 4.8★ rating', type: 'vendor' },
                { id: 2, title: 'Trending menu', description: 'South Indian Deluxe Package', type: 'menu' },
                { id: 3, title: 'Special offer', description: '20% off on weekend bookings', type: 'offer' }
            ]);

        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        const icons = {
            pending: <ClockCircleOutlined />,
            confirmed: <ThunderboltOutlined />,
            completed: <StarOutlined />,
            cancelled: <ClockCircleOutlined />
        };
        return icons[status] || <ClockCircleOutlined />;
    };

    const getActivityIcon = (iconName) => {
        const icons = {
            'CalendarOutlined': <CalendarOutlined />,
            'FileTextOutlined': <FileTextOutlined />,
            'CheckCircleOutlined': <CheckCircleOutlined />,
            'StarOutlined': <StarOutlined />,
            'UserOutlined': <UserOutlined />,
            'ShopOutlined': <ShopOutlined />,
            'ShoppingCartOutlined': <ShoppingCartOutlined />,
            'BellOutlined': <BellOutlined />,
            'MessageOutlined': <MessageOutlined />,
            'SettingOutlined': <SettingOutlined />,
        };
        return icons[iconName] || <BellOutlined />;
    };

    if (loading) {
        return (
            <div className="px-4 sm:px-6 py-6">
                <div className="max-w-7xl mx-auto">
                    <Row gutter={[16, 16]}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Col xs={24} sm={12} lg={8} key={i}>
                                <Card bodyStyle={{ padding: 12 }}>
                                    <Skeleton active paragraph={{ rows: 3 }} />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4 sm:px-6 py-6">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <Title level={2} className="!mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                Welcome back, {user?.name}! 👋
                            </Title>
                            <Text type="secondary" className="text-base sm:text-lg">
                                Here's your catering journey overview
                            </Text>
                        </div>
                        <div className="flex gap-2">
                            <Badge count={3} size="small">
                                <Button icon={<BellOutlined />} shape="circle" />
                            </Badge>
                            <Link to="/get-started">
                                <Button type="primary" icon={<CalendarOutlined />}>
                                    New Booking
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Loyalty Card */}
                {false && stats.loyaltyPoints > 0 && (
                    <Alert
                        message={
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <TrophyOutlined className="text-yellow-500 text-xl" />
                                    <div>
                                        <strong>Loyalty Rewards: {stats.loyaltyPoints} points</strong>
                                        <div className="text-sm">You've saved ₹{stats.savedAmount.toFixed(2)} so far!</div>
                                    </div>
                                </div>
                                <Button type="primary" size="small" icon={<GiftOutlined />}>
                                    Redeem
                                </Button>
                            </div>
                        }
                        type="success"
                        className="mb-6"
                        showIcon={false}
                    />
                )}

                {/* Enhanced Stats Cards */}
                <Row gutter={[12, 12]} className="mb-6 sm:mb-8">
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500" bodyStyle={{ padding: 12 }}>
                            <Statistic
                                title="Total Orders"
                                value={stats.totalBookings}
                                prefix={<ShoppingCartOutlined className="text-blue-500" />}
                                suffix={
                                    <div className="text-xs text-green-500 flex items-center">
                                        <RiseOutlined /> +12%
                                    </div>
                                }
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-orange-500" bodyStyle={{ padding: 12 }}>
                            <Statistic
                                title="Pending Requests"
                                value={stats.pendingRequests}
                                prefix={<CalendarOutlined className="text-orange-500" />}
                                valueStyle={{ color: '#faad14' }}
                            />
                            {stats.pendingRequests > 0 && (
                                <div className="text-xs text-gray-500 mt-1">
                                    Awaiting vendor response
                                </div>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500" bodyStyle={{ padding: 12 }}>
                            <Statistic
                                title="Completed Orders"
                                value={stats.completedBookings}
                                prefix={<StarOutlined className="text-green-500" />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                            <div className="mt-2">
                                <Rate disabled defaultValue={4.5} className="text-xs" />
                                <Text type="secondary" className="ml-2 text-xs">Avg Rating</Text>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-purple-500" bodyStyle={{ padding: 12 }}>
                            <Statistic
                                title="Total Spent"
                                value={stats.totalSpent}
                                prefix={<FireOutlined className="text-purple-500" />}
                                precision={2}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                Loyalty Points: {stats.loyaltyPoints}
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[12, 12]}>
                    {/* Recent Bookings */}
                    <Col xs={24} lg={12}>
                        <Card 
                            title={
                                <div className="flex items-center gap-2">
                                    <ShoppingCartOutlined />
                                    Recent Orders
                                </div>
                            } 
                            extra={<Link to="/bookings">View All</Link>}
                            className="h-full"
                            size="small"
                            bodyStyle={{ padding: 12 }}
                        >
                            {bookings.length > 0 ? (
                                <List
                                    size="small"
                                    itemLayout="horizontal"
                                    dataSource={bookings}
                                    renderItem={(booking) => (
                                        <List.Item
                                            actions={[
                                                <Link key="view" to={`/bookings/${booking.id}`}>
                                                    <Button type="link" size="small">View</Button>
                                                </Link>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Avatar 
                                                        icon={getStatusIcon(booking.status)} 
                                                        style={{ 
                                                            backgroundColor: sharedStatusColor(booking.status) === 'orange' ? '#faad14' : 
                                                                              sharedStatusColor(booking.status) === 'green' ? '#52c41a' : '#1890ff'
                                                        }}
                                                    />
                                                }
                                                title={
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">Order #{booking.id}</span>
                                                        <Tag color={sharedStatusColor(booking.status)} size="small">
                                                            {booking.status}
                                                        </Tag>
                                                    </div>
                                                }
                                                description={
                                                    <div className="text-sm">
                                                        <div className="text-gray-600"> {booking.event_date}</div>
                                                        <div className="text-green-600 font-medium">₹{booking.total_amount}</div>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )} 
                                />
                            ) : (
                                <Empty 
                                    description="No orders yet" 
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                >
                                    <Link to="/menu">
                                        <Button type="primary">Browse Menu</Button>
                                    </Link>
                                </Empty>
                            )}
                        </Card>
                    </Col>

                    {/* Booking Requests & Drafts */}
                    <Col xs={24} lg={12}>
                        <Card 
                            title={
                                <div className="flex items-center gap-2">
                                    <CalendarOutlined />
                                    My Requests & Drafts
                                </div>
                            } 
                            className="h-full"
                            size="small"
                            bodyStyle={{ padding: 12 }}
                        >
                            {drafts.length > 0 ? (
                                <List
                                    size="small"
                                    itemLayout="horizontal"
                                    dataSource={drafts}
                                    renderItem={(draft) => {
                                        const isCompleted = draft.step === 5;
                                        const isInProgress = draft.step > 0 && draft.step < 5;
                                        
                                        return (
                                            <List.Item
                                                actions={[
                                                    isCompleted ? (
                                                        <Tag key="status" color="blue" size="small">
                                                            Submitted
                                                        </Tag>
                                                    ) : isInProgress ? (
                                                        <Space key="actions">
                                                            <Link to={`/get-started?edit=${draft.id}`}>
                                                                <Button size="small" type="primary">
                                                                    Continue
                                                                </Button>
                                                            </Link>
                                                            <Tag color="orange" size="small">
                                                                Draft
                                                            </Tag>
                                                        </Space>
                                                    ) : (
                                                        <Tag key="status" color="default" size="small">
                                                            New
                                                        </Tag>
                                                    )
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            icon={<CalendarOutlined />} 
                                                            style={{ backgroundColor: isCompleted ? '#52c41a' : '#1890ff' }}
                                                        />
                                                    }
                                                    title={
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{draft.eventType || 'Event'}</span>
                                                            <Tag color="orange" size="small">
                                                                {draft.guestCount} guests
                                                            </Tag>
                                                        </div>
                                                    }
                                                    description={
                                                        <div className="text-sm">
                                                            <div className="text-gray-600">{draft.date} at {draft.time}</div>
                                                            <div className="text-gray-600">{draft.venue}</div>
                                                            <div className="text-blue-600 font-medium">
                                                                {draft.selectedDishes?.length || 0} dishes selected
                                                            </div>
                                                            {isInProgress && (
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    Step {draft.step} of 5
                                                                </div>
                                                            )}
                                                        </div>
                                                    }
                                                />
                                            </List.Item>
                                        );
                                    }} 
                                />
                            ) : (
                                <Empty 
                                    description="No booking requests yet" 
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                >
                                    <Link to="/get-started">
                                        <Button type="primary">Create Booking Request</Button>
                                    </Link>
                                </Empty>
                            )}
                        </Card>
                    </Col>

                    {/* Booking Requests (Leads) */}
                    <Col xs={24} lg={12}>
                        <Card 
                            title={
                                <div className="flex items-center gap-2">
                                    <MessageOutlined />
                                    Booking Requests
                                </div>
                            } 
                            extra={<Link to="/chat">View All</Link>}
                            className="h-full"
                            size="small"
                            bodyStyle={{ padding: 12 }}
                        >
                            {leads.length > 0 ? (
                                <List
                                    size="small"
                                    itemLayout="horizontal"
                                    dataSource={leads}
                                    renderItem={(lead) => {
                                        const statusColors = {
                                            pending: 'orange',
                                            quoted: 'blue',
                                            accepted: 'green',
                                            rejected: 'red',
                                            completed: 'purple'
                                        };
                                        
                                        return (
                                            <List.Item
                                                actions={[
                                                    <Link key="chat" to={`/chat?lead=${lead.id}`}>
                                                        <Button size="small" type="primary" icon={<MessageOutlined />}>
                                                            Chat
                                                        </Button>
                                                    </Link>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            icon={<MessageOutlined />} 
                                                            style={{ backgroundColor: '#1890ff' }}
                                                        />
                                                    }
                                                    title={
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{lead.event_type}</span>
                                                            <Tag color={sharedStatusColor(lead.status) || 'default'} size="small">
                                                                {lead.status}
                                                            </Tag>
                                                        </div>
                                                    }
                                                    description={
                                                        <div className="text-sm">
                                                            <div className="text-gray-600">{lead.event_date} at {lead.event_time}</div>
                                                            <div className="text-gray-600">{lead.venue}</div>
                                                            <div className="text-blue-600 font-medium">
                                                                {lead.vendor?.name || 'Vendor'}
                                                            </div>
                                                            {lead.messages && lead.messages.length > 0 && (
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {lead.messages.length} messages
                                                                </div>
                                                            )}
                                                        </div>
                                                    }
                                                />
                                            </List.Item>
                                        );
                                    }} 
                                />
                            ) : (
                                <Empty 
                                    description="No booking requests yet" 
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                >
                                    <Link to="/get-started">
                                        <Button type="primary">Create Booking Request</Button>
                                    </Link>
                                </Empty>
                            )}
                        </Card>
                    </Col>

                    {/* Activity Timeline & Quick Actions */}
                    <Col xs={24} lg={12}>
                        <Row gutter={[12, 12]} className="h-full">
                            <Col span={24}>
                                <Card 
                                    title={
                                        <div className="flex items-center gap-2">
                                            <ClockCircleOutlined />
                                            Recent Activity
                                        </div>
                                    }
                                    size="small"
                                    bodyStyle={{ padding: 12 }}
                                >
                                    <Timeline
                                        items={recentActivity.map(activity => ({
                                            dot: <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs">
                                                {activity.icon}
                                            </div>,
                                            children: (
                                                <div>
                                                    <div className="font-medium">{activity.action}</div>
                                                    <div className="text-sm text-gray-600">{activity.details}</div>
                                                    <div className="text-xs text-gray-400">{activity.time}</div>
                                                </div>
                                            )
                                        }))} 
                                    />
                                </Card>
                            </Col>
                            <Col span={24}>
                                <Card title="Quick Actions" size="small" bodyStyle={{ padding: 12 }}>
                                    <Row gutter={[8, 8]}>
                                        <Col span={12}>
                                            <Link to="/menu">
                                                <Button type="primary" block icon={<ShoppingCartOutlined />} size="small">
                                                    Browse Menu
                                                </Button>
                                            </Link>
                                        </Col>
                                        {singleVendor ? (
                                            <Col span={12}>
                                                <Link to={`/vendors/${singleVendor.id}`}>
                                                    <Button block icon={<UserOutlined />} size="small">
                                                        View Vendor
                                                    </Button>
                                                </Link>
                                            </Col>
                                        ) : (
                                            <Col span={12}>
                                                <Link to="/vendors">
                                                    <Button block icon={<UserOutlined />} size="small">
                                                        Find Vendors
                                                    </Button>
                                                </Link>
                                            </Col>
                                        )}
                                        <Col span={12}>
                                            <Link to="/booking">
                                                <Button block icon={<CalendarOutlined />} size="small">
                                                    New Booking
                                                </Button>
                                            </Link>
                                        </Col>
                                        <Col span={12}>
                                            <Link to="/profile">
                                                <Button block icon={<UserOutlined />} size="small">
                                                    Profile
                                                </Button>
                                            </Link>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Quotes Section */}
                {quotes.length > 0 && (
                    <Row gutter={[12, 12]} className="mt-6">
                        <Col xs={24}>
                            <Card 
                                title={
                                    <div className="flex items-center gap-2">
                                        <FileTextOutlined />
                                        Pending Quotes
                                        <Badge count={quotes.filter(q => q.status === 'pending').length} />
                                    </div>
                                } 
                                extra={<Link to="/quotes">View All</Link>}
                                size="small"
                                bodyStyle={{ padding: 12 }}
                            >
                                <List
                                    size="small"
                                    itemLayout="horizontal"
                                    dataSource={quotes.slice(0, 3)}
                                    renderItem={(quote) => {
                                        const statusColors = {
                                            pending: 'orange',
                                            accepted: 'green',
                                            rejected: 'red',
                                            expired: 'gray'
                                        };
                                        
                                        return (
                                            <List.Item
                                                actions={[
                                                    (quote.status === 'pending' || quote.status === 'quoted') ? (
                                                        <Space key="actions">
                                                            <Button 
                                                                size="small" 
                                                                type="primary" 
                                                                icon={<CheckCircleOutlined />}
                                                                onClick={() => {
                                                                    // Navigate to quote detail page
                                                                    window.location.href = `/quotes/${quote.id}`;
                                                                }}
                                                            >
                                                                Accept
                                                            </Button>
                                                            <Button 
                                                                size="small" 
                                                                danger
                                                                icon={<CloseCircleOutlined />}
                                                                onClick={() => {
                                                                    // Navigate to quote detail page
                                                                    window.location.href = `/quotes/${quote.id}`;
                                                                }}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Space>
                                                    ) : (
                                                        <Link key="view" to={`/quotes/${quote.id}`}>
                                                            <Button size="small">View</Button>
                                                        </Link>
                                                    )
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            icon={<FileTextOutlined />} 
                                                            style={{ 
                                                                backgroundColor: statusColors[quote.status] === 'orange' ? '#faad14' : 
                                                                                  statusColors[quote.status] === 'green' ? '#52c41a' : 
                                                                                  statusColors[quote.status] === 'red' ? '#ff4d4f' : '#d9d9d9'
                                                            }}
                                                        />
                                                    }
                                                    title={
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{quote.quote_number || `Quote #${quote.id}`}</span>
                                                            <Tag color={statusColors[quote.status]} size="small">
                                                                {quote.status}
                                                            </Tag>
                                                        </div>
                                                    }
                                                    description={
                                                        <div className="text-sm">
                                                            <div className="text-gray-600">
                                                                Vendor: {quote.vendor?.name || quote.vendor?.business_name || 'Unknown'}
                                                            </div>
                                                            <div className="text-gray-600">
                                                                Event: {quote.event_details?.event_type || 'Catering Event'}
                                                            </div>
                                                            <div className="text-green-600 font-medium">
                                                                ₹{quote.total_amount || quote.subtotal || 0}
                                                            </div>
                                                            {quote.valid_until && (
                                                                <div className="text-xs text-gray-500">
                                                                    Valid until: {new Date(quote.valid_until).toLocaleDateString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    }
                                                />
                                            </List.Item>
                                        );
                                    }} 
                                />
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Recommendations */}
                <div className="mt-6">
                    <RecommendationEngine />
                </div>

                {/* Gamification */}
                <div className="mt-6">
                    <GamificationSystem />
                </div>

                {/* Calendar Integration */}
                <div className="mt-6">
                    <CalendarIntegration />
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
